import type { Edge, Node } from '@xyflow/react';
import type { ElectricConnection } from '../../../types/connections';

export function getTerminalNodeId(instanceId: number | string, contactId: number | string): string {
  return `terminal-${instanceId}-${contactId}`;
}

export function getCableNodeId(instanceId: number | string): string {
  return String(instanceId);
}

export function mapConnectionToEdge(conn: ElectricConnection, schemaType: 'main' | 'terminal'): Edge {
  let rawWireProps: any = conn.wire_properties || {};
  try {
    if (typeof rawWireProps === 'string') rawWireProps = JSON.parse(rawWireProps);
    if (typeof rawWireProps === 'string') rawWireProps = JSON.parse(rawWireProps);
  } catch (_e) {
    rawWireProps = {};
  }
  const edgeType = (rawWireProps && (rawWireProps.type || rawWireProps.connectionType)) || 'default';

  const isTerminalSchema = schemaType === 'terminal';

  const sourceNodeId = isTerminalSchema
    ? getTerminalNodeId(conn.from_instance_id, (conn as any).from_contact_id)
    : getCableNodeId(conn.from_instance_id);
  const targetNodeId = isTerminalSchema
    ? getCableNodeId(conn.to_instance_id)
    : getCableNodeId(conn.to_instance_id);

  const sourceHandle = `${(conn as any).from_contact_id}-source`;
  const targetHandle = `${(conn as any).to_contact_id}-target`;

  // Parse path_points
  let pathPoints: any[] = [];
  let isManualRoute = false;
  try {
    const pp: any = (conn as any).path_points;
    const parsed = typeof pp === 'string' ? JSON.parse(pp) : pp;
    if (Array.isArray(parsed)) {
      pathPoints = parsed.map((p: any) => ({ x: p.x, y: p.y, type: 'waypoint' }));
      isManualRoute = !!(conn as any).is_manual_route;
    }
  } catch (_e) {}

  return {
    id: `connection-${conn.id}`,
    source: sourceNodeId,
    target: targetNodeId,
    sourceHandle,
    targetHandle,
    // Use custom edge if manual route or explicit custom type
    type: (isManualRoute || pathPoints.length > 0 || edgeType === 'custom') ? 'custom' : edgeType,
    style: {
      stroke: rawWireProps.color || rawWireProps.style_properties?.stroke || '#007bff',
      strokeWidth: rawWireProps.stroke_width || rawWireProps.style_properties?.strokeWidth || 2,
      strokeDasharray: rawWireProps.stroke_dasharray || rawWireProps.style_properties?.strokeDasharray,
      ...rawWireProps.style_properties,
    },
    animated: false,
    selectable: false,
    data: {
      connectionId: conn.id,
      pathPoints,
      isManualRoute,
      wireProperties: {
        ...rawWireProps,
        type: edgeType,
      },
    },
  } as Edge;
}

export function filterConnectionsByVisibleNodes(
  connections: ElectricConnection[],
  nodesToRender: Node[],
  schemaType: 'main' | 'terminal'
): ElectricConnection[] {
  const visibleNodeIds = new Set(nodesToRender.map((n: Node) => n.id));

  const isTerminalSchema = schemaType === 'terminal';

  return connections.filter((conn: ElectricConnection) => {
    const sourceNodeId = isTerminalSchema
      ? getTerminalNodeId(conn.from_instance_id, (conn as any).from_contact_id)
      : getCableNodeId(conn.from_instance_id);
    const targetNodeId = getCableNodeId(conn.to_instance_id);
    return visibleNodeIds.has(sourceNodeId) && visibleNodeIds.has(targetNodeId);
  });
}

export function buildAutoTerminalEdges(
  cableTerminalConnections: any[],
  nodesToRender: Node[]
): Edge[] {
  const nodeIds = new Set(nodesToRender.map((n: Node) => n.id));

  return (cableTerminalConnections || [])
    .filter((c: any) => c.cable_device_type === 'cable')
    .map((c: any) => {
      const terminalNodeId = getTerminalNodeId(c.terminal_instance_id, c.terminal_contact_id);
      const cableNodeId = getCableNodeId(c.cable_instance_id);
      if (!nodeIds.has(terminalNodeId) || !nodeIds.has(cableNodeId)) return null;

      // Source must be a SOURCE handle, target must be a TARGET handle
      // Terminal bottom is SOURCE, Cable top is TARGET
      const sourceHandle = `${c.terminal_contact_id}-bottom-source`;
      const targetHandle = `${c.cable_contact_id}-target`;

      // Parse path points and wire properties
      // Normalize to RoutePoint format used by CustomEdge: { x, y, type: 'waypoint' }
      let pathPoints: any[] = [];
      let isManualRoute = false;
      try {
        const pp: any = c.path_points;
        const parsed = typeof pp === 'string' ? JSON.parse(pp) : pp;
        if (Array.isArray(parsed)) {
          pathPoints = parsed.map((p: any) => ({ x: p.x, y: p.y, type: 'waypoint' }));
          isManualRoute = !!c.is_manual_route;
        }
      } catch (_e) {}

      // Parse wire_properties for style and type
      let rawWireProps: any = c.wire_properties || {};
      try {
        if (typeof rawWireProps === 'string') rawWireProps = JSON.parse(rawWireProps);
        if (typeof rawWireProps === 'string') rawWireProps = JSON.parse(rawWireProps);
      } catch (_e) {
        rawWireProps = {};
      }
      const edgeType = (rawWireProps && (rawWireProps.type || rawWireProps.connectionType)) || 'default';

      return {
        id: `connection-${c.connection_id}`,
        source: terminalNodeId,
        target: cableNodeId,
        sourceHandle,
        targetHandle,
        // Use custom edge when manual route or explicit path points present, otherwise honor wire type
        type: (isManualRoute || pathPoints.length > 0) ? 'custom' : edgeType,
        style: {
          stroke: rawWireProps.color || rawWireProps.style_properties?.stroke || '#007bff',
          strokeWidth: rawWireProps.stroke_width || rawWireProps.style_properties?.strokeWidth || 2,
          strokeDasharray: rawWireProps.stroke_dasharray || rawWireProps.style_properties?.strokeDasharray,
          ...rawWireProps.style_properties,
        },
        animated: false,
        selectable: false,
        data: {
          connectionId: c.connection_id,
          pathPoints,
          isManualRoute,
          wireProperties: {
            ...rawWireProps,
            type: edgeType,
          },
        },
      } as Edge;
    })
    .filter(Boolean) as Edge[];
}


